import {
  ApolloCache,
  gql,
  useMutation,
  useQuery,
} from "@apollo/client";
import { useMemo } from "react";
import {
  CatalogImageOwner,
  FLOORPLANNER_CATALOG_PRODUCT_FIELDS,
} from "../features/floorplan3d/catalog/catalogQueries";

export type UserFavoriteProductsQuery = {
  userFavoriteProducts: CatalogImageOwner[];
};

/**
 * Favorites return SEK catalog prices (no server currency conversion).
 * ProductLibraryCard converts via formatDisplayPrice, same as the product library.
 */
export const UserFavoriteProductsDocument = gql`
  ${FLOORPLANNER_CATALOG_PRODUCT_FIELDS}
  query FloorplannerUserFavoriteProducts {
    userFavoriteProducts {
      ...FloorplannerCatalogProductFields
    }
  }
`;

export const AddProductToFavoritesDocument = gql`
  mutation FloorplannerAddProductToFavorites($id: String!) {
    addProductToFavorites(id: $id)
  }
`;

export const RemoveProductFromFavoritesDocument = gql`
  mutation FloorplannerRemoveProductFromFavorites($id: String!) {
    removeProductFromFavorites(id: $id)
  }
`;

/** Drop ROOT_QUERY.userFavoriteProducts so cached favorites do not stay stale. */
const invalidateFavoriteProductsCache = (cache: ApolloCache<unknown>) => {
  cache.evict({
    id: "ROOT_QUERY",
    fieldName: "userFavoriteProducts",
  });
  cache.gc();
};

/**
 * Account product favorites (same list as the moodboard editor).
 * Prices stay in SEK; the UI converts with the floorplan display currency.
 */
export function useUserFavoriteProducts(skip = false) {
  return useQuery<UserFavoriteProductsQuery>(UserFavoriteProductsDocument, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    skip,
  });
}

/** True when the product id is in the user's favorite list. */
export function useIsProductFavorite(productId: string): boolean {
  const { data } = useUserFavoriteProducts();
  return useMemo(
    () =>
      Boolean(data?.userFavoriteProducts?.some((product) => product.id === productId)),
    [data?.userFavoriteProducts, productId],
  );
}

/**
 * Placeable favorites only: ready published 3D asset with a GLB URL.
 * Editor favorites may include products without 3D models.
 */
export function isPlaceableFavoriteProduct(
  product: CatalogImageOwner,
): boolean {
  return (
    product.active3DAsset?.status === "ready" &&
    Boolean(product.active3DAsset.glbUrl)
  );
}

/**
 * Add / remove a product from account favorites with optimistic cache updates.
 */
export function useToggleProductFavorite() {
  const [addFavorite, addState] = useMutation(AddProductToFavoritesDocument);
  const [removeFavorite, removeState] = useMutation(
    RemoveProductFromFavoritesDocument,
  );

  const toggle = async (
    product: CatalogImageOwner,
    currentlyFavorite: boolean,
  ) => {
    if (currentlyFavorite) {
      return removeFavorite({
        variables: { id: product.id },
        optimisticResponse: {
          __typename: "Mutation",
          removeProductFromFavorites: product.id,
        },
        update: (cache) => {
          const existing = cache.readQuery<UserFavoriteProductsQuery>({
            query: UserFavoriteProductsDocument,
          });
          if (!existing) return;
          const next = existing.userFavoriteProducts.filter(
            (item) => item.id !== product.id,
          );
          invalidateFavoriteProductsCache(cache);
          cache.writeQuery({
            query: UserFavoriteProductsDocument,
            data: { userFavoriteProducts: next },
          });
        },
      });
    }

    return addFavorite({
      variables: { id: product.id },
      optimisticResponse: {
        __typename: "Mutation",
        addProductToFavorites: product.id,
      },
      update: (cache) => {
        const existing = cache.readQuery<UserFavoriteProductsQuery>({
          query: UserFavoriteProductsDocument,
        });
        if (!existing) {
          invalidateFavoriteProductsCache(cache);
          cache.writeQuery({
            query: UserFavoriteProductsDocument,
            data: { userFavoriteProducts: [product] },
          });
          return;
        }
        if (existing.userFavoriteProducts.some((item) => item.id === product.id)) {
          return;
        }
        invalidateFavoriteProductsCache(cache);
        cache.writeQuery({
          query: UserFavoriteProductsDocument,
          data: {
            userFavoriteProducts: [product, ...existing.userFavoriteProducts],
          },
        });
      },
    });
  };

  return {
    toggle,
    loading: addState.loading || removeState.loading,
  };
}
